/**
 * Loading skeleton component for content placeholders.
 * 
 * @module components/ui/LoadingSkeleton
 */

import { cn } from '../../lib/utils';
import type { LoadingSkeletonProps } from '../../types';

/**
 * Skeleton loader for content placeholders.
 * 
 * @param props - Skeleton properties
 * @returns Skeleton element
 */
export function LoadingSkeleton({
  variant = 'text',
  width,
  height,
  className,
}: LoadingSkeletonProps) {
  const baseClasses = 'animate-pulse bg-gray-200';
  
  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
      style={style}
      aria-hidden="true"
    />
  );
}

/**
 * Table skeleton for loading states.
 */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex gap-4 px-6 py-3 bg-gray-50 rounded-t-lg">
        {Array.from({ length: columns }).map((_, i) => (
          <LoadingSkeleton key={i} className="flex-1 h-4" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 px-6 py-4 border-b border-gray-100">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <LoadingSkeleton
              key={colIndex}
              className="flex-1 h-4"
              width={colIndex === 0 ? '60%' : undefined}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Card skeleton for loading states.
 */
export function CardSkeleton() {
  return (
    <div className="card p-6 space-y-4">
      <LoadingSkeleton className="h-6 w-1/3" />
      <LoadingSkeleton className="h-4 w-full" />
      <LoadingSkeleton className="h-4 w-2/3" />
    </div>
  );
}

/**
 * Stats grid skeleton for dashboard.
 */
export function StatsGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card p-6">
          <LoadingSkeleton className="h-4 w-1/2 mb-4" />
          <LoadingSkeleton className="h-8 w-1/3 mb-2" />
          <LoadingSkeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}
